import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { api } from '../api/endpoints';
import { LoadingPill } from '../components/LoadingPill';
import { PermitsResult } from '../features/permits/PermitsResult';
import { AddressSearchBox } from '../features/search/AddressSearchBox';
import { type PermitSearch, SearchBox } from '../features/search/SearchBox';
import { searchString } from '../lib/searchParams';

/** Search by address, permit number or APN (#171). The address page has the
 *  parcel map, so there's no separate Parcel map page for prospects. */
function SearchPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const results = useQuery({
    queryKey: ['search-permits', search.kind, search.q, search.state],
    enabled: Boolean(search.kind && search.q),
    queryFn: () =>
      api.searchPermits({
        kind: search.kind as PermitSearch['kind'],
        q: search.q ?? '',
        ...(search.state ? { state: search.state } : {}),
      }),
  });
  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          Search permits
        </Typography>
        <SearchBox
          address={<AddressSearchBox autoFocus />}
          onSearch={(s) => navigate({ to: '/search', search: s })}
          initial={{
            ...(search.kind ? { kind: search.kind } : {}),
            ...(search.q ? { q: search.q } : {}),
            ...(search.state ? { state: search.state } : {}),
          }}
        />
      </Paper>
      {search.kind && search.q ? (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {search.kind === 'permit' ? 'Permit number' : 'APN'} {search.q}
            {search.state ? ` in ${search.state}` : ''}
          </Typography>
          <PermitsResult
            result={results}
            loading={<LoadingPill label="Searching…" />}
            failed="The search failed. Try again in a moment."
            showAddress
          />
        </>
      ) : null}
    </Box>
  );
}

export const Route = createFileRoute('/search')({
  validateSearch: z.object({
    kind: z.enum(['permit', 'apn']).optional(),
    q: searchString.optional(),
    state: searchString.optional(),
  }),
  component: SearchPage,
});
